import Log from "@/Logger";
import { Refresh } from "@mui/icons-material";
import { useTheme, Box, Button, Menu, MenuItem, Paper, Typography, useMediaQuery, Stack, Tooltip, IconButton, keyframes } from "@mui/material";
import { useEffect, useRef, useState } from "react";

// 回転アニメーションを定義
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

/**
 * 目次情報の定義
 */
export type toc = {
    pageId: string;
    name: string;
    index: number; // 0始まり
};
export type DropdownNavProps = {
    docName?: string;
    tocArray: toc[];
    currentPageId: string;
    isRefreshEnabled: boolean;
    handleRefreshDocInfo: () => Promise<void>;
    handleSelectToc: (toc: toc) => void;
};

const DropdownNavMenu: React.FC<{ tocArray: toc[], currentToc?: toc, handleSelectToc: (toc: toc) => void }> = (
    { tocArray, currentToc, handleSelectToc }
) => {
    // メニューのアンカー要素
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    //目次のリストの開閉を行う
    const handleTocListOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleTocListClose = () => {
        setAnchorEl(null);
    };
    return (<Box
        className='PdfViewerDropdownNavMenu'
        sx={{
            position: "relative",
        }}
    >
        <Button
            variant="text"
            size="small"
            onClick={handleTocListOpen}
            sx={{
                minWidth: 0,
                px: 1,
                fontWeight: "medium",
                pointerEvents: !currentToc ? "none" : "auto",
                textWrap: "nowrap",
            }}
        >
            {currentToc ? `${currentToc.name} ▼` : "読み込み中..."}
        </Button>
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleTocListClose}
        >
            {tocArray.map((toc) => (
                <MenuItem
                    key={toc.pageId}
                    onClick={() => handleSelectToc(toc)}
                >
                    {toc.name}
                </MenuItem>
            ))}
        </Menu>
    </Box >);
}

export const DropdownNav: React.FC<DropdownNavProps> = ({ docName, currentPageId, tocArray, isRefreshEnabled, handleRefreshDocInfo, handleSelectToc }) => {
    const theme = useTheme();
    // MUIのブレークポイント sm 以下ならスマホ扱い
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const currentToc = tocArray.find(toc => toc.pageId === currentPageId);

    if (isMobile) {
        // スマホの場合
        return (<>
            <Stack className='PdfViewerDropdownNav-1' direction="row" spacing={0} alignItems="center" >
                <Tooltip title={isRefreshEnabled ? "文書情報を再読込" : "読込中..."}>
                    <span>
                        { }
                        <IconButton
                            size="small"
                            onClick={handleRefreshDocInfo}
                            disabled={!isRefreshEnabled}
                            aria-label={isRefreshEnabled ? "文書情報を再読込" : "読込中..."}
                        >
                            <Refresh fontSize="small" sx={!isRefreshEnabled ? { animation: `${spin} 2s linear infinite` } : {}} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" sx={{ overflow: "hidden", textWrap: "auto" }}>
                    {docName || "文書名が設定されていません"}
                </Typography>
            </Stack>
            <Stack className='PdfViewerDropdownNav-2' direction="row" spacing={0} alignItems="center">
                <DropdownNavMenu
                    tocArray={tocArray}
                    currentToc={currentToc}
                    handleSelectToc={handleSelectToc}
                />
            </Stack >
        </>);
    } else {
        // PC・タブレットの場合
        return (<>
            <Stack className='PdfViewerDropdownNav-1' direction="row" spacing={0} alignItems="center"            >
                <Tooltip title={isRefreshEnabled ? "文書情報を再読込" : "読込中..."}>
                    <span>
                        { }
                        <IconButton
                            size="small"
                            onClick={handleRefreshDocInfo}
                            disabled={!isRefreshEnabled}
                            aria-label={isRefreshEnabled ? "文書情報を再読込" : "読込中..."}
                        >
                            <Refresh fontSize="small" sx={!isRefreshEnabled ? { animation: `${spin} 2s linear infinite` } : {}} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ overflow: "hidden", textWrap: "auto" }}>
                    {docName || "文書名が設定されていません"}
                </Typography>
                <DropdownNavMenu
                    tocArray={tocArray}
                    currentToc={currentToc}
                    handleSelectToc={handleSelectToc}
                />
            </Stack>
            <Stack className='PdfViewerDropdownNav-2' direction="row" spacing={0} alignItems="center" flexGrow={1} height={10}>
            </Stack>
        </>);
    }
}

export type SideNavProps = {
    docName?: string;
    tocArray: toc[];
    currentPageId: string;
    handleSelectToc: (toc: toc) => void;
};

export const SideNav: React.FC<SideNavProps> = ({ docName, currentPageId, tocArray, handleSelectToc }) => {
    const [sidebarWidth, setSidebarWidth] = useState<number>(220); // 目次サイドバーの幅
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const startXRef = useRef<number>(0);
    const startWidthRef = useRef<number>(sidebarWidth);

    /**
     * サイドバーのリサイズ処理
     * マウスの移動に合わせてサイドバーの幅を変更する
     * マウスアップでリサイズ終了
     */
    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e: MouseEvent) => {
            const dx = startXRef.current - e.clientX;
            let newWidth = startWidthRef.current + dx;
            newWidth = Math.max(140, Math.min(newWidth, 400)); // 最小/最大幅
            setSidebarWidth(newWidth);
        };
        const handleMouseUp = () => {
            setIsResizing(false);
            document.body.style.cursor = "";
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizing, sidebarWidth]);

    // ドラッグ開始
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsResizing(true);
        startXRef.current = e.clientX;
        startWidthRef.current = sidebarWidth;
        document.body.style.cursor = "col-resize";
    };

    return (<>

        {/* リサイズバー */}
        <Box
            className='PdfViewerResizer'
            sx={{
                width: 8,
                cursor: "col-resize",
                background: isResizing ? "#1976d2" : "transparent",
                transition: "background 0.2s",
                zIndex: 30,
                display: { xs: "none", lg: "block" },
                userSelect: "none",
            }}
            onMouseDown={handleMouseDown}
        />

        {/* 目次サイドバー */}
        <Paper
            className='PdfViewerSideNav'
            elevation={1}
            sx={{
                position: "sticky",
                top: 80,
                right: 0,
                width: sidebarWidth, height: "100%",
                minWidth: 140,
                maxWidth: 400,
                overflowY: "auto",
                ml: 0,
                p: 2,
                background: "#fff",
                zIndex: 20,
                alignSelf: "flex-start",
                display: { xs: "none", lg: "block" },
                scrollbarWidth: "auto",
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    background: "#ccc",
                    borderRadius: "4px",
                },
                "&::-webkit-scrollbar-track": {
                    background: "#f0f0f0",
                    borderRadius: "4px",
                },
                boxSizing: "border-box",
            }}
        >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {docName || "目次"}
            </Typography>
            {tocArray.map((toc: toc) => (
                <Box
                    key={toc.pageId}
                    sx={{
                        mb: 1,
                        cursor: "pointer",
                        "&:hover": { background: "#f5f5f5" },
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        transition: "background 0.2s",
                        background: toc.pageId === currentPageId ? "#e0e0e0" : "transparent",
                    }}
                    onClick={() => handleSelectToc(toc)}
                >
                    <Typography variant="body2">{toc.name}</Typography>
                </Box>
            ))}
        </Paper>
    </>);
}
